import type { PCPStepConfig } from '@/constants/pcp/types';
import { PCPDataContext } from '@/pages/pcp/PCPortal';
import { formatStringToSha256Hash } from '@/utils/formatStringToSha256Hash';
import { Typography } from '@mui/material';
import { useContext, useMemo } from 'react';
import Stages from '../../components/components/Stages';
import Steps from '../../components/components/Steps';
import WhatToBring from '../../components/components/WhatToBring';
import NurseConsult from '../steps/nurse/NurseConsult';
import NursePreConsultForm from '../steps/nurse/NursePreConsultForm';

export default function PreNurse() {
  const { userData } = useContext(PCPDataContext);

  const isInitialNurseConsultBooked = userData?.preNurseConsultation.consultationBooked;
  const isInitialNurseConsultCompleted = userData?.preNurseConsultation.completed;
  const hasInitialNurseBookingUrl = userData?.preNurseConsultation.calcomBookingUrl !== null;
  const shaUserEmail = formatStringToSha256Hash(userData?.prefillFormData.email);
  const isPatientIntiatedNurseBooking =
    !isInitialNurseConsultBooked && !isInitialNurseConsultCompleted && hasInitialNurseBookingUrl;

  const nurseStepsConfig: PCPStepConfig[] = useMemo(
    () => [
      {
        stepComponent: <NurseConsult />,
        stepId: 'nurse_consult',
        title: 'Nurse consultation',
        urlApiPath: 'preNurseConsultation.calcomBookingUrl',
        completedApiPath: 'preNurseConsultation.completed',
        subtitleActive: isPatientIntiatedNurseBooking ? 'Make a booking' : 'View and reschedule',
        buttonTextActive: isPatientIntiatedNurseBooking ? 'Book consultation' : 'View consultation',
        buttonId: isPatientIntiatedNurseBooking ? 'book-nurse-consult-step-button' : 'view-nurse-consult-step-button',
        buttonTextCompleted: 'Completed',
        ga4Events: {
          buttonClickEvents: isPatientIntiatedNurseBooking
            ? [
                {
                  event: 'ga4_3__start_booking',
                  event_category: 'cal.com',
                  event_label: 'Initial Consultation',
                  promocode: '',
                  sha_email: shaUserEmail || '',
                },
              ]
            : [
                {
                  event: 'click_view_nurse_consultation',
                  clickId: 'view-nurse-consult-step-button',
                  dlv_click_id: 'view-nurse-consult-step-button',
                  event_category: 'button-click',
                },
              ],
        },
      },
      {
        stepComponent: <NursePreConsultForm />,
        stepId: 'nurse_pre_consult_form',
        title: 'Pre-consultation form',
        buttonId: 'pre-consult-form-step-button',
        optimisticSubmit: true,
        optimisticSubmitTimeout: 180000,
        requiredApiPath: 'preNurseConsultation.preConsultationForm.required',
        completedApiPath: 'preNurseConsultation.preConsultationForm.completed',
        subtitleActive: '3 mins to complete',
        buttonTextActive: 'Complete now',
        buttonTextCompleted: 'Completed',
        ga4Events: {
          buttonClickEvents: [
            {
              event: 'click_pre_consult_start',
              clickId: 'pre-consult-form-step-button',
              dlv_click_id: 'pre-consult-form-step-button',
              event_category: 'button-click',
            },
          ],
          formCompleteEvents: [
            {
              event: 'pre_consult_complete',
              clickId: 'pre-consult-complete',
              dlv_click_id: 'pre-consult-complete',
              event_category: 'form',
            },
          ],
        },
      },
    ],
    [isPatientIntiatedNurseBooking, shaUserEmail],
  );

  return (
    <>
      <Typography variant="h5" component="h1" sx={(theme) => ({ color: theme.palette.primary.main })}>
        Your Alternaleaf journey
      </Typography>

      <Stages stage={1} />

      <Typography variant="body1" mb="1.5rem">
        Complete these before your nurse consult
      </Typography>

      <Steps stepsConfig={nurseStepsConfig} />

      <WhatToBring />

      <Typography
        variant="h5"
        component="h2"
        sx={(theme) => ({ color: theme.palette.primary.main, marginBottom: theme.spacing(6) })}
      >
        What to expect after your nurse consultation
      </Typography>
      <Typography variant="body1">
        Your nurse will talk with you about your treatment goals, answer any questions you have, and get everything
        ready for your doctor consultation. If you&apos;re eligible for our personalised alternative healthcare service,
        your nurse will schedule your doctor appointment to consider your circumstances.
      </Typography>
    </>
  );
}
